/* ===================================================================
   THE FLOAT CLUB — fc-checkout.js
   A real, self-contained checkout: contact, shipping address, delivery
   method, order review, order placement, confirmation.

   WHY THIS EXISTS
   Shopify hosts its own checkout, so when SHOP_DOMAIN + PRODUCT_HANDLE
   are set the Checkout button hands off to Shopify and this file stays
   out of the way -- collecting an address ourselves and then sending
   the shopper to Shopify would make them type it twice.

   Until that store exists, this is the checkout. It captures a complete
   order and delivers it. What it does NOT do is charge a card, because
   taking card payments requires a merchant account in the operator's
   own name -- that cannot be created from here by anyone. So the flow
   is honest about it: the order is captured and confirmed, and payment
   follows by link. That is a real ordering process, not a mock.

   WHERE ORDERS GO, in priority order:
     1. ORDER_ENDPOINT  — any POST-accepting URL (Formspree, Basin, a
                          Vercel function, a Zapier/Make webhook).
     2. ORDER_EMAIL     — opens the shopper's mail client, prefilled.
   The order is also written to localStorage so a failed send is
   recoverable rather than silently lost.
=================================================================== */
(function (global) {
  "use strict";

  /* ---- CONFIGURE ME -------------------------------------------------
     ORDER_ENDPOINT is the one to set for a hands-off flow: the shopper
     never leaves the page and the order lands in your tool of choice.
     ORDER_EMAIL is the no-signup fallback. Change it to a business
     inbox before you run traffic -- it is shown to customers on the
     confirmation screen.
  ------------------------------------------------------------------- */
  var ORDER_ENDPOINT = "";
  var ORDER_EMAIL    = "benybenh@gmail.com";

  /* ---- PAYMENT MARKS ------------------------------------------------
     Add a method here ONLY once that method can actually be taken. A
     Visa mark on a page that cannot charge a Visa card is a false
     representation to the shopper: they see the logo, expect to pay by
     card, and get an email instead. It also reads as a scam signal,
     which costs more conversions than the badges win.

     The moment a processor is live, list what it accepts and the row
     appears. Nothing else needs changing.
       e.g. PAYMENT_METHODS = ["visa","mastercard","amex","paypal","applepay","googlepay"];
  ------------------------------------------------------------------- */
  var PAYMENT_METHODS = [];

  /* Delivery. Prices in GBP; everything renders through FC.fmt so the
     shopper sees them in the currency they picked. */
  var FREE_SHIPPING_OVER_GBP = 65;
  var SHIPPING = [
    { id: "standard", name: "Standard delivery", detail: "3–5 working days", gbp: 3.95, freeOver: true },
    { id: "express",  name: "Express delivery",  detail: "1–2 working days", gbp: 7.95, freeOver: false }
  ];

  var COUNTRIES = ["United Kingdom", "Ireland", "France", "Germany", "Spain", "Italy",
    "Netherlands", "Belgium", "Portugal", "Denmark", "Sweden", "Norway", "Poland",
    "United States", "Canada", "Australia", "New Zealand", "Other"];

  var STEPS = ["Contact", "Delivery", "Review"];

  var state = { step: 0, shipping: "standard", data: {}, errors: {}, placed: null };

  function fmt(gbp) { return (global.FC && global.FC.fmt) ? global.FC.fmt(gbp) : "$" + gbp.toFixed(2); }
  function esc(s) {
    return String(s == null ? "" : s).replace(/[&<>"']/g, function (c) {
      return { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c];
    });
  }

  /* ---- money ------------------------------------------------------- */
  function items() { return (global.FC && global.FC.items) ? global.FC.items() : []; }
  function qty() { return items().reduce(function (n, i) { return n + i.qty; }, 0); }
  function goodsGBP() { return global.FC ? global.FC.totalGBP() : 0; }
  function shippingGBP() {
    var m = SHIPPING.filter(function (s) { return s.id === state.shipping; })[0] || SHIPPING[0];
    if (m.freeOver && goodsGBP() >= FREE_SHIPPING_OVER_GBP) return 0;
    return m.gbp;
  }
  function grandGBP() { return goodsGBP() + shippingGBP(); }

  /* ---- validation --------------------------------------------------
     Rules are per-field so an error can be shown against the field that
     caused it rather than as a list at the top, which is where errors
     go to be ignored. */
  var RULES = {
    email:     { label: "Email", test: function (v) { return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v); },
                 msg: "Enter an email we can send the order confirmation to." },
    firstName: { label: "First name", test: function (v) { return v.length >= 1; }, msg: "Enter your first name." },
    lastName:  { label: "Last name",  test: function (v) { return v.length >= 1; }, msg: "Enter your last name." },
    phone:     { label: "Phone", optional: true, test: function (v) { return !v || v.replace(/\D/g, "").length >= 7; },
                 msg: "That phone number looks too short." },
    address1:  { label: "Address", test: function (v) { return v.length >= 3; }, msg: "Enter your street address." },
    address2:  { label: "Apartment, suite (optional)", optional: true, test: function () { return true; } },
    city:      { label: "Town or city", test: function (v) { return v.length >= 2; }, msg: "Enter your town or city." },
    postcode:  { label: "Postcode", test: function (v) { return v.length >= 3; }, msg: "Enter your postcode." },
    country:   { label: "Country", test: function (v) { return !!v; }, msg: "Choose a country." }
  };
  var STEP_FIELDS = [
    ["email", "firstName", "lastName", "phone"],
    ["country", "address1", "address2", "city", "postcode"]
  ];

  function validateStep(n) {
    var fields = STEP_FIELDS[n] || [];
    state.errors = {};
    fields.forEach(function (k) {
      var r = RULES[k], v = (state.data[k] || "").trim();
      if (r.optional && !v) return;
      if (!r.test(v)) state.errors[k] = r.msg;
    });
    return Object.keys(state.errors).length === 0;
  }

  /* ---- styles ------------------------------------------------------ */
  function injectStyles() {
    if (document.getElementById("fc-co-css")) return;
    var s = document.createElement("style");
    s.id = "fc-co-css";
    s.textContent = [
      "#fc-co{position:fixed;inset:0;z-index:200;display:none;background:#F2F9FA;overflow-y:auto;color:#05222B;",
      "font-family:Archivo,sans-serif;-webkit-overflow-scrolling:touch}",
      "#fc-co.is-open{display:block}",
      ".fc-co-wrap{max-width:1120px;margin:0 auto;padding:22px clamp(16px,4vw,40px) 80px;",
      "display:grid;grid-template-columns:1fr;gap:28px}",
      "@media(min-width:900px){.fc-co-wrap{grid-template-columns:1.25fr 0.85fr;gap:48px;padding-top:34px}",
      ".fc-co-sum{position:sticky;top:34px;align-self:start}}",
      ".fc-co-top{display:flex;align-items:center;justify-content:space-between;gap:16px;margin-bottom:6px}",
      ".fc-co-brand{font-family:Anton,sans-serif;font-size:20px;letter-spacing:0.04em;text-transform:uppercase}",
      ".fc-co-x{width:44px;height:44px;display:inline-flex;align-items:center;justify-content:center;",
      "border-radius:12px;border:1px solid rgba(5,34,43,0.14);background:#FFF;cursor:pointer;color:#05222B}",
      ".fc-co-x:hover{background:rgba(5,34,43,0.05)}",
      ".fc-co-steps{display:flex;gap:8px;margin:14px 0 22px;flex-wrap:wrap}",
      ".fc-co-step{font-size:11px;letter-spacing:0.14em;text-transform:uppercase;font-weight:600;",
      "padding:8px 12px;border-radius:999px;background:rgba(5,34,43,0.06);color:rgba(5,34,43,0.78)}",
      ".fc-co-step.is-on{background:#FFC53D;color:#05222B}",
      ".fc-co-step.is-done{background:rgba(255,197,61,0.28);color:#7A5200}",
      ".fc-co-h{font-family:Anton,sans-serif;font-size:clamp(24px,3.4vw,34px);line-height:1.06;",
      "text-transform:uppercase;margin:0 0 4px}",
      ".fc-co-sub{font-size:13px;line-height:1.7;color:rgba(5,34,43,0.78);margin:0 0 22px}",
      ".fc-co-grid{display:grid;grid-template-columns:1fr 1fr;gap:14px}",
      ".fc-co-f{display:flex;flex-direction:column;gap:6px}",
      ".fc-co-f.wide{grid-column:1 / -1}",
      "@media(max-width:560px){.fc-co-grid{grid-template-columns:1fr}.fc-co-f{grid-column:1 / -1}}",
      ".fc-co-l{font-size:11px;letter-spacing:0.12em;text-transform:uppercase;font-weight:600;color:rgba(5,34,43,0.78)}",
      ".fc-co-i{height:50px;padding:0 14px;border-radius:12px;border:1px solid rgba(5,34,43,0.16);",
      "background:#FFF;color:#05222B;font-family:Archivo,sans-serif;font-size:16px;width:100%;box-sizing:border-box}",
      "select.fc-co-i{cursor:pointer;-webkit-appearance:none;appearance:none;",
      "background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%2305222B' stroke-width='2.6'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E\");",
      "background-repeat:no-repeat;background-position:right 14px center;padding-right:38px}",
      ".fc-co-i:focus{outline:3px solid #FFC53D;outline-offset:1px;border-color:rgba(5,34,43,0.3)}",
      ".fc-co-i[aria-invalid='true']{border-color:#B3261E;background:#FFF7F6}",
      ".fc-co-err{font-size:12px;line-height:1.5;color:#B3261E;min-height:0}",
      ".fc-co-ship{display:flex;flex-direction:column;gap:10px;margin-top:8px}",
      ".fc-co-opt{display:flex;align-items:center;gap:12px;padding:14px;border-radius:14px;cursor:pointer;",
      "border:1px solid rgba(5,34,43,0.16);background:#FFF;min-height:44px}",
      ".fc-co-opt.is-on{border-color:#D69814;background:rgba(255,197,61,0.16)}",
      ".fc-co-dot{width:18px;height:18px;border-radius:999px;border:2px solid rgba(5,34,43,0.32);flex:0 0 auto}",
      ".fc-co-opt.is-on .fc-co-dot{border-color:#D69814;background:#FFC53D;box-shadow:inset 0 0 0 3px #FFF}",
      ".fc-co-nav{display:flex;gap:12px;align-items:center;margin-top:26px;flex-wrap:wrap}",
      ".fc-co-btn{min-height:52px;padding:0 26px;border-radius:999px;border:none;cursor:pointer;background:#FFC53D;",
      "color:#05222B;font-family:Archivo,sans-serif;font-weight:700;font-size:14px;letter-spacing:0.03em;",
      "text-transform:uppercase;display:inline-flex;align-items:center;gap:10px}",
      ".fc-co-btn:hover{background:#FFE08A}",
      ".fc-co-btn[disabled]{background:#C8D8DC;color:rgba(5,34,43,0.78);cursor:not-allowed;pointer-events:none}",
      ".fc-co-back{background:transparent;border:1px solid rgba(5,34,43,0.16);min-height:52px;padding:0 20px;",
      "border-radius:999px;cursor:pointer;font-family:Archivo,sans-serif;font-weight:600;font-size:13px;color:#05222B}",
      ".fc-co-back:hover{background:rgba(5,34,43,0.05)}",
      ".fc-co-sum-card{background:#FFF;border:1px solid rgba(5,34,43,0.12);border-radius:20px;padding:20px;",
      "box-shadow:0 10px 30px rgba(5,34,43,0.06)}",
      ".fc-co-line{display:flex;justify-content:space-between;gap:14px;font-size:13px;padding:7px 0;color:rgba(5,34,43,0.85)}",
      ".fc-co-line.total{font-family:Anton,sans-serif;font-size:21px;color:#05222B;padding-top:14px;margin-top:8px;",
      "background:linear-gradient(180deg,rgba(5,34,43,0.08),rgba(5,34,43,0.08)) top left/100% 1px no-repeat}",
      ".fc-co-item{display:flex;gap:12px;align-items:flex-start;padding:10px 0}",
      ".fc-co-sw{width:38px;height:38px;border-radius:10px;flex:0 0 auto;border:1px solid rgba(5,34,43,0.12)}",
      ".fc-co-rev{background:#FFF;border:1px solid rgba(5,34,43,0.12);border-radius:16px;padding:16px;margin-bottom:12px}",
      ".fc-co-rev h4{margin:0 0 6px;font-size:11px;letter-spacing:0.14em;text-transform:uppercase;color:rgba(5,34,43,0.78)}",
      ".fc-co-rev p{margin:0;font-size:14px;line-height:1.65}",
      ".fc-co-edit{background:none;border:none;padding:0;color:#0B4C5C;font-weight:600;font-size:12px;",
      "cursor:pointer;text-decoration:underline;min-height:44px}",
      ".fc-co-note{font-size:12px;line-height:1.65;color:rgba(5,34,43,0.78);margin-top:14px}",
      ".fc-co-ok{max-width:620px;margin:0 auto;text-align:center;padding:40px 0}",
      ".fc-co-tick{width:66px;height:66px;border-radius:999px;background:#FFC53D;display:inline-flex;",
      "align-items:center;justify-content:center;margin-bottom:18px}",
      ".fc-co-trust{list-style:none;margin:26px 0 0;padding:18px 0 0;display:grid;grid-template-columns:1fr 1fr;gap:14px 18px;background:linear-gradient(180deg,rgba(5,34,43,0.10),rgba(5,34,43,0.10)) top left/100% 1px no-repeat}",
      "@media(max-width:560px){.fc-co-trust{grid-template-columns:1fr}}",
      ".fc-co-trust li{display:flex;gap:10px;align-items:flex-start;font-size:12px;line-height:1.55;color:rgba(5,34,43,0.78)}",
      ".fc-co-trust strong{font-weight:700;font-size:12.5px;color:#05222B;letter-spacing:0.01em}",
      ".fc-co-ti{width:30px;height:30px;flex:0 0 auto;border-radius:9px;display:inline-flex;align-items:center;justify-content:center;background:rgba(255,197,61,0.28);border:1px solid rgba(214,152,20,0.45)}",
      ".fc-co-pay{margin-top:18px;display:flex;flex-direction:column;gap:8px}",
      ".fc-co-marks{display:flex;flex-wrap:wrap;gap:8px}",
      ".fc-co-mark{height:26px;padding:0 9px;border-radius:6px;background:#FFF;border:1px solid rgba(5,34,43,0.14);display:inline-flex;align-items:center;justify-content:center;font-family:Archivo,sans-serif;font-size:11px}",
      ".fc-co-ref{font-family:Anton,sans-serif;font-size:26px;letter-spacing:0.06em;margin:14px 0 6px}"
    ].join("");
    document.head.appendChild(s);
  }

  /* ---- markup ------------------------------------------------------ */
  function field(key, type, auto, extra) {
    var r = RULES[key], v = esc(state.data[key] || ""), err = state.errors[key];
    var wide = extra && extra.wide ? " wide" : "";
    var input;
    if (type === "select") {
      input = '<select class="fc-co-i" id="fc-co-' + key + '" name="' + key + '" autocomplete="' + auto + '"' +
        (err ? ' aria-invalid="true"' : "") + '>' +
        '<option value="">Choose…</option>' +
        COUNTRIES.map(function (c) {
          return '<option value="' + esc(c) + '"' + (state.data[key] === c ? " selected" : "") + '>' + esc(c) + '</option>';
        }).join("") + '</select>';
    } else {
      input = '<input class="fc-co-i" id="fc-co-' + key + '" name="' + key + '" type="' + type + '"' +
        ' value="' + v + '" autocomplete="' + auto + '"' +
        (extra && extra.inputmode ? ' inputmode="' + extra.inputmode + '"' : "") +
        (err ? ' aria-invalid="true" aria-describedby="fc-co-e-' + key + '"' : "") + '>';
    }
    return '<div class="fc-co-f' + wide + '">' +
      '<label class="fc-co-l" for="fc-co-' + key + '">' + esc(r.label) + '</label>' + input +
      '<span class="fc-co-err" id="fc-co-e-' + key + '">' + (err ? esc(err) : "") + '</span></div>';
  }

  function summaryHTML() {
    var list = items(), n = qty();
    var disc = global.FC ? global.FC.bundleDiscountGBP(n) : 0;
    var ship = shippingGBP();
    return '<div class="fc-co-sum"><div class="fc-co-sum-card">' +
      '<div class="fc-co-l" style="margin-bottom:10px">Your order — ' + n + (n === 1 ? " vest" : " vests") + '</div>' +
      list.map(function (i) {
        return '<div class="fc-co-item">' +
          '<div class="fc-co-sw" style="background:' + swatch(i.colour) + '"></div>' +
          '<div style="flex:1 1 auto;min-width:0">' +
            '<div style="font-weight:600;font-size:13px">' + esc(i.colour) + '</div>' +
            '<div style="font-size:12px;color:rgba(5,34,43,0.78)">Size ' + esc(i.size) + ' · Qty ' + i.qty + '</div>' +
          '</div>' +
          '<div style="font-weight:700;font-size:13px">' + fmt(i.qty * global.FC.unitGBP) + '</div></div>';
      }).join("") +
      '<div class="fc-co-line"><span>Subtotal</span><span>' + fmt(n * global.FC.unitGBP) + '</span></div>' +
      (disc > 0 ? '<div class="fc-co-line" style="color:#7A5200"><span>Bundle saving</span><span>−' + fmt(disc) + '</span></div>' : "") +
      '<div class="fc-co-line"><span>Delivery</span><span>' + (ship === 0 ? "Free" : fmt(ship)) + '</span></div>' +
      '<div class="fc-co-line total"><span>Total</span><span>' + fmt(grandGBP()) + '</span></div>' +
      (global.FC.isConverted() ? '<p class="fc-co-note">Converted from USD at an indicative rate. You are charged in USD.</p>' : "") +
      '</div></div>';
  }

  /* ---- trust row ----------------------------------------------------
     Every claim here is one the business actually makes elsewhere on the
     site. Nothing aspirational, nothing borrowed from a competitor. The
     last one is the strongest reassurance available right now precisely
     because it is true: no card details are handled on this page at all.
  ------------------------------------------------------------------- */
  var TRUST = [
    { t: "30-day returns", d: "Money back if the fit is wrong",
      p: "M12 3 4 6v5.5c0 4.6 3.2 8.4 8 9.5 4.8-1.1 8-4.9 8-9.5V6z|m9 12 2 2 4-4" },
    { t: "Free size swap", d: "Within 90 days if you sized him wrong",
      p: "M3 12a9 9 0 0 1 15.5-6.2M21 12a9 9 0 0 1-15.5 6.2|M18 3v3h-3|M6 21v-3h3" },
    { t: "Ships in 24 hours", d: "Order before 2pm, tracked worldwide",
      p: "M3 7h11v8H3z|M14 10h4l3 3v2h-7z|M7 19a2 2 0 1 0 0-4 2 2 0 0 0 0 4|M18 19a2 2 0 1 0 0-4 2 2 0 0 0 0 4" },
    { t: "No card details here", d: "Nothing is charged on this page",
      p: "M5 11V8a7 7 0 0 1 14 0v3|M4 11h16v10H4z" }
  ];

  function icon(paths) {
    return '<svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="#0B4C5C" ' +
      'stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
      paths.split("|").map(function (d) { return '<path d="' + d + '"></path>'; }).join("") + '</svg>';
  }

  function trustHTML() {
    return '<ul class="fc-co-trust">' + TRUST.map(function (b) {
      return '<li><span class="fc-co-ti">' + icon(b.p) + '</span>' +
        '<span><strong>' + esc(b.t) + '</strong><br>' + esc(b.d) + '</span></li>';
    }).join("") + '</ul>';
  }

  var MARK = {
    visa:      { label: "VISA",       w: 42, style: "font-style:italic;font-weight:800;letter-spacing:0.02em;color:#1A1F71" },
    mastercard:{ label: "mastercard", w: 58, style: "font-weight:700;font-size:9px;color:#231F20" },
    amex:      { label: "AMEX",       w: 42, style: "font-weight:800;color:#006FCF" },
    paypal:    { label: "PayPal",     w: 46, style: "font-style:italic;font-weight:800;color:#003087" },
    applepay:  { label: "Apple Pay",  w: 54, style: "font-weight:600;color:#000" },
    googlepay: { label: "G Pay",      w: 44, style: "font-weight:600;color:#5F6368" },
    shoppay:   { label: "Shop Pay",   w: 54, style: "font-weight:700;color:#5A31F4" },
    klarna:    { label: "Klarna",     w: 46, style: "font-weight:700;color:#0B051D" }
  };

  function paymentsHTML() {
    var live = PAYMENT_METHODS.filter(function (k) { return MARK[k]; });
    if (!live.length) return "";   // no processor -> no claim
    return '<div class="fc-co-pay"><span class="fc-co-l">Accepted</span><div class="fc-co-marks">' +
      live.map(function (k) {
        var m = MARK[k];
        return '<span class="fc-co-mark" style="min-width:' + m.w + 'px;' + m.style + '">' +
          esc(m.label) + '</span>';
      }).join("") + '</div></div>';
  }

  var SWATCH = {
    "Signal Red": "linear-gradient(140deg,#E23A2E 0%,#E23A2E 58%,#2B3A40 100%)",
    "Sunrise Yellow": "linear-gradient(140deg,#F5A800 0%,#F5A800 58%,#2B3A40 100%)",
    "Lagoon Blue": "linear-gradient(140deg,#2BA6DE 0%,#2BA6DE 58%,#2B3A40 100%)",
    "Deep Violet": "linear-gradient(140deg,#6B4E9E 0%,#6B4E9E 58%,#2B3A40 100%)",
    "Hibiscus Pink": "linear-gradient(140deg,#E0568C 0%,#E0568C 58%,#2B3A40 100%)",
    "Reef Print": "linear-gradient(140deg,#2E8B7A 0%,#2E8B7A 58%,#2B3A40 100%)"
  };
  function swatch(c) { return SWATCH[c] || "#0B4C5C"; }

  function stepsHTML() {
    return '<div class="fc-co-steps">' + STEPS.map(function (s, i) {
      var cls = i === state.step ? " is-on" : (i < state.step ? " is-done" : "");
      return '<span class="fc-co-step' + cls + '">' + (i + 1) + ' ' + s + '</span>';
    }).join("") + '</div>';
  }

  function bodyHTML() {
    if (state.placed) return confirmHTML();
    var s = state.step, h = "";
    if (s === 0) {
      h = '<h2 class="fc-co-h">Contact</h2>' +
        '<p class="fc-co-sub">We send the order confirmation here. No marketing unless you ask for it.</p>' +
        '<div class="fc-co-grid">' +
          field("email", "email", "email", { wide: true, inputmode: "email" }) +
          field("firstName", "text", "given-name") +
          field("lastName", "text", "family-name") +
          field("phone", "tel", "tel", { wide: true, inputmode: "tel" }) +
        '</div>';
    } else if (s === 1) {
      h = '<h2 class="fc-co-h">Delivery</h2>' +
        '<p class="fc-co-sub">Where the vest goes.</p>' +
        '<div class="fc-co-grid">' +
          field("country", "select", "country-name", { wide: true }) +
          field("address1", "text", "address-line1", { wide: true }) +
          field("address2", "text", "address-line2", { wide: true }) +
          field("city", "text", "address-level2") +
          field("postcode", "text", "postal-code", { inputmode: "text" }) +
        '</div>' +
        '<div class="fc-co-l" style="margin-top:22px">Delivery method</div>' +
        '<div class="fc-co-ship">' + SHIPPING.map(function (m) {
          var free = m.freeOver && goodsGBP() >= FREE_SHIPPING_OVER_GBP;
          return '<label class="fc-co-opt' + (state.shipping === m.id ? " is-on" : "") + '" data-ship="' + m.id + '">' +
            '<input type="radio" name="fc-ship" value="' + m.id + '" ' +
              (state.shipping === m.id ? "checked" : "") + ' style="position:absolute;opacity:0;width:1px;height:1px">' +
            '<span class="fc-co-dot" aria-hidden="true"></span>' +
            '<span style="flex:1 1 auto"><span style="font-weight:600;font-size:14px">' + m.name + '</span>' +
            '<br><span style="font-size:12px;color:rgba(5,34,43,0.78)">' + m.detail + '</span></span>' +
            '<span style="font-weight:700;font-size:14px">' + (free ? "Free" : fmt(m.gbp)) + '</span></label>';
        }).join("") + '</div>';
    } else {
      var d = state.data;
      var m = SHIPPING.filter(function (x) { return x.id === state.shipping; })[0];
      h = '<h2 class="fc-co-h">Review</h2>' +
        '<p class="fc-co-sub">Check it over before you place the order.</p>' +
        '<div class="fc-co-rev"><h4>Contact</h4><p>' + esc(d.firstName + " " + d.lastName) + '<br>' +
          esc(d.email) + (d.phone ? '<br>' + esc(d.phone) : "") + '</p>' +
          '<button type="button" class="fc-co-edit" data-goto="0">Edit</button></div>' +
        '<div class="fc-co-rev"><h4>Ship to</h4><p>' + esc(d.address1) +
          (d.address2 ? '<br>' + esc(d.address2) : "") + '<br>' + esc(d.city) + ' ' + esc(d.postcode) +
          '<br>' + esc(d.country) + '</p>' +
          '<button type="button" class="fc-co-edit" data-goto="1">Edit</button></div>' +
        '<div class="fc-co-rev"><h4>Delivery</h4><p>' + esc(m.name) + ' · ' + esc(m.detail) + '</p>' +
          '<button type="button" class="fc-co-edit" data-goto="1">Edit</button></div>' +
        '<p class="fc-co-note">Placing the order reserves your sizes and sends you a confirmation. ' +
        'Card payment is not taken on this page — we reply with a secure payment link, and nothing ' +
        'is charged until you use it.</p>';
    }
    var last = state.step === STEPS.length - 1;
    return stepsHTML() + h +
      '<div class="fc-co-nav">' +
        (state.step > 0 ? '<button type="button" class="fc-co-back" data-back>Back</button>' : "") +
        '<button type="button" class="fc-co-btn" data-next>' +
          (last ? "Place order — " + fmt(grandGBP()) : "Continue") +
          '<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" aria-hidden="true"><path d="m9 6 6 6-6 6"/></svg>' +
        '</button>' +
      '</div>' + trustHTML() + paymentsHTML();
  }

  function confirmHTML() {
    var o = state.placed;
    return '<div class="fc-co-ok"><div class="fc-co-tick">' +
      '<svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="#05222B" stroke-width="3" ' +
      'stroke-linecap="round" stroke-linejoin="round"><path d="m4 12 6 6L20 6"/></svg></div>' +
      '<h2 class="fc-co-h">Order received</h2>' +
      '<div class="fc-co-ref">' + esc(o.ref) + '</div>' +
      '<p class="fc-co-sub">Keep that reference. We have your order for ' + o.qty +
        (o.qty === 1 ? " vest" : " vests") + ' totalling ' + esc(o.total) + '.</p>' +
      '<p class="fc-co-sub">' + esc(o.deliveryNote) + '</p>' +
      '<button type="button" class="fc-co-btn" data-done>Back to the shop</button></div>';
  }

  /* ---- mount ------------------------------------------------------- */
  function root() {
    var r = document.getElementById("fc-co");
    if (r) return r;
    r = document.createElement("div");
    r.id = "fc-co";
    r.setAttribute("role", "dialog");
    r.setAttribute("aria-modal", "true");
    r.setAttribute("aria-label", "Checkout");
    document.body.appendChild(r);
    return r;
  }

  function render() {
    var r = root();
    r.innerHTML =
      '<div class="fc-co-wrap"><div><div class="fc-co-top">' +
        '<div class="fc-co-brand">The Float Club</div>' +
        '<button type="button" class="fc-co-x" data-close aria-label="Close checkout">' +
        '<svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" ' +
        'stroke-linecap="round"><path d="M18 6 6 18M6 6l12 12"/></svg></button></div>' +
        bodyHTML() + '</div>' +
        (state.placed ? "" : summaryHTML()) +
      '</div>';
  }

  function open() {
    if (!items().length) return;
    injectStyles();
    state.step = 0; state.errors = {}; state.placed = null;
    render();
    var r = root();
    r.classList.add("is-open");
    document.body.style.overflow = "hidden";
    var f = r.querySelector(".fc-co-i");
    if (f) f.focus();
  }

  function close() {
    var r = document.getElementById("fc-co");
    if (!r) return;
    r.classList.remove("is-open");
    document.body.style.overflow = "";
  }

  /* capture what the shopper typed before any re-render throws it away */
  function harvest() {
    var r = document.getElementById("fc-co");
    if (!r) return;
    r.querySelectorAll(".fc-co-i").forEach(function (i) {
      state.data[i.name] = i.value;
    });
  }

  /* ---- placing the order ------------------------------------------- */
  function buildOrder() {
    var list = items(), n = qty();
    var m = SHIPPING.filter(function (x) { return x.id === state.shipping; })[0];
    var ref = "TFC-" + Date.now().toString(36).toUpperCase().slice(-6);
    return {
      ref: ref,
      placedAt: new Date().toISOString(),
      customer: {
        firstName: state.data.firstName, lastName: state.data.lastName,
        email: state.data.email, phone: state.data.phone || ""
      },
      shipTo: {
        address1: state.data.address1, address2: state.data.address2 || "",
        city: state.data.city, postcode: state.data.postcode, country: state.data.country
      },
      delivery: { method: m.name, detail: m.detail, gbp: shippingGBP() },
      items: list,
      qty: n,
      currencyShown: global.FC.currency(),
      totals: { goodsGBP: goodsGBP(), shippingGBP: shippingGBP(), grandGBP: grandGBP() },
      total: fmt(grandGBP())
    };
  }

  function orderText(o) {
    return "ORDER " + o.ref +
      "\n\n" + o.items.map(function (i) { return i.qty + " x " + i.colour + " / size " + i.size; }).join("\n") +
      "\n\nGoods: £" + o.totals.goodsGBP.toFixed(2) +
      "\nDelivery: £" + o.totals.shippingGBP.toFixed(2) + " (" + o.delivery.method + ")" +
      "\nTOTAL: £" + o.totals.grandGBP.toFixed(2) +
      "\n\n" + o.customer.firstName + " " + o.customer.lastName +
      "\n" + o.customer.email + (o.customer.phone ? "\n" + o.customer.phone : "") +
      "\n\n" + o.shipTo.address1 + (o.shipTo.address2 ? "\n" + o.shipTo.address2 : "") +
      "\n" + o.shipTo.city + " " + o.shipTo.postcode + "\n" + o.shipTo.country;
  }

  function archive(o) {
    // a send can fail; the order must not evaporate with it
    try {
      var all = JSON.parse(localStorage.getItem("fc_orders") || "[]");
      all.push(o);
      localStorage.setItem("fc_orders", JSON.stringify(all.slice(-25)));
    } catch (e) {}
  }

  function place() {
    var o = buildOrder();
    archive(o);

    function finish(note) {
      o.deliveryNote = note;
      state.placed = o;
      if (global.FC && global.FC.clear) global.FC.clear();
      render();
      root().scrollTop = 0;
    }

    // Two different things happen depending on how orders are delivered, and
    // the shopper is told the true one. With an endpoint the order is already
    // with us. With mailto it is sitting in THEIR mail client, unsent, and
    // claiming we received it would be a lie.
    var sentViaEndpoint = "Your order is with us. We will email " + o.customer.email +
      " to confirm, with a secure payment link. Nothing is charged until you use it.";
    var sentViaMail = "Your email app has opened with the order ready to send. Send it and we will " +
      "confirm by return, with a secure payment link. Nothing is charged until you use it.";

    if (ORDER_ENDPOINT) {
      fetch(ORDER_ENDPOINT, {
        method: "POST",
        headers: { "Accept": "application/json", "Content-Type": "application/json" },
        body: JSON.stringify(o)
      }).then(function (r) {
        finish(r.ok ? sentViaEndpoint : "Your order is saved under " + o.ref +
          ". If you do not hear from us within an hour, email " + ORDER_EMAIL + " quoting it.");
      }).catch(function () {
        finish("Your order is saved under " + o.ref + ". Please email " + ORDER_EMAIL + " quoting it.");
      });
      return;
    }

    if (ORDER_EMAIL) {
      // opens the shopper's mail client with the order already written out,
      // so it genuinely reaches a person rather than a dead handler
      global.location.href = "mailto:" + ORDER_EMAIL +
        "?subject=" + encodeURIComponent("Float Club order " + o.ref) +
        "&body=" + encodeURIComponent(orderText(o));
      finish(sentViaMail);
      return;
    }

    finish("Your order reference is " + o.ref + ". Ordering is not connected to an inbox yet.");
  }

  /* ---- events ------------------------------------------------------ */
  document.addEventListener("click", function (e) {
    if (!e.target.closest) return;

    if (e.target.closest("[data-close]") && e.target.closest("#fc-co")) { close(); return; }
    if (e.target.closest("[data-done]")) { close(); return; }

    var ship = e.target.closest("[data-ship]");
    if (ship && e.target.closest("#fc-co")) {
      harvest();
      state.shipping = ship.getAttribute("data-ship");
      render();
      return;
    }

    var goto = e.target.closest("[data-goto]");
    if (goto) { harvest(); state.step = Number(goto.getAttribute("data-goto")); state.errors = {}; render(); return; }

    if (e.target.closest("[data-back]")) { harvest(); state.step = Math.max(0, state.step - 1); state.errors = {}; render(); return; }

    if (e.target.closest("[data-next]")) {
      harvest();
      if (state.step < STEPS.length - 1) {
        if (!validateStep(state.step)) {
          render();
          var first = document.querySelector("#fc-co [aria-invalid='true']");
          if (first) first.focus();
          return;
        }
        state.step++;
        render();
        root().scrollTop = 0;
      } else {
        // re-check everything, not just the last screen: a shopper can
        // reach Review and then edit a field back into an invalid state
        if (!validateStep(0)) { state.step = 0; render(); return; }
        if (!validateStep(1)) { state.step = 1; render(); return; }
        var b = document.querySelector("#fc-co [data-next]");
        if (b) { b.setAttribute("disabled", "true"); b.textContent = "Placing…"; }
        place();
      }
    }
  });

  document.addEventListener("keydown", function (e) {
    if (e.key === "Escape") close();
  });

  global.FC_CHECKOUT = { open: open, close: close, isConfigured: function () { return !!(ORDER_ENDPOINT || ORDER_EMAIL); } };

})(window);
